import { supabase } from '../lib/supabase';
import { UserContext, DocumentSavePayload } from '../contexts/EmbedContext';

interface SaveDocumentParams {
  documentGenerationId: string;
  documentType: string;
  propertyId?: string;
  userId: string;
  organizationId?: string;
  subscriptionTier: string;
  standardContent: string;
  enhancedContent?: string;
  premiumContent?: string;
  formData: Record<string, any>;
}

interface SyncResult {
  success: boolean;
  parentDocumentId?: string;
  error?: string;
}

class DocumentSaveService {
  async saveDocumentLocally(params: SaveDocumentParams): Promise<{ id: string; success: boolean }> {
    try {
      const { data, error } = await supabase
        .from('document_generations')
        .insert({
          id: params.documentGenerationId,
          property_id: params.propertyId,
          user_session_id: params.userId,
          organization_id: params.organizationId,
          document_type: params.documentType,
          form_data: params.formData,
          standard_document: params.standardContent,
          enhanced_document: params.enhancedContent,
          premium_document: params.premiumContent,
          status: 'completed',
          access_tier: 'premium',
          sync_status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;

      return { id: data.id, success: true };
    } catch (error) {
      console.error('Error saving document locally:', error);
      throw error;
    }
  }

  async syncWithParentApp(
    saveToParent: (payload: DocumentSavePayload) => Promise<{ success: boolean; parentDocumentId?: string }>,
    params: SaveDocumentParams
  ): Promise<SyncResult> {
    try {
      const payload: DocumentSavePayload = {
        documentGenerationId: params.documentGenerationId,
        documentType: params.documentType,
        propertyId: params.propertyId,
        standardContent: params.standardContent,
        enhancedContent: params.enhancedContent,
        premiumContent: params.premiumContent,
        formData: params.formData,
        subscriptionTier: params.subscriptionTier,
      };

      const result = await saveToParent(payload);

      if (result.success && result.parentDocumentId) {
        await this.updateSyncStatus(params.documentGenerationId, 'synced', result.parentDocumentId);
        return { success: true, parentDocumentId: result.parentDocumentId };
      } else {
        await this.updateSyncStatus(params.documentGenerationId, 'failed');
        return { success: false, error: 'Failed to sync with parent app' };
      }
    } catch (error) {
      console.error('Error syncing with parent app:', error);
      await this.updateSyncStatus(params.documentGenerationId, 'failed');
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async updateSyncStatus(documentGenerationId: string, status: string, parentDocumentId?: string) {
    try {
      const updateData: any = { sync_status: status };
      if (parentDocumentId) {
        updateData.parent_document_id = parentDocumentId;
      }

      const { error } = await supabase
        .from('document_generations')
        .update(updateData)
        .eq('id', documentGenerationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating sync status:', error);
    }
  }

  async saveSigningSession(
    documentGenerationId: string,
    envelopeId: string,
    parties: any[],
    initiatedBy: string
  ) {
    try {
      const { data, error } = await supabase
        .from('signing_sessions')
        .insert({
          document_generation_id: documentGenerationId,
          envelope_id: envelopeId,
          signing_parties: parties,
          status: 'created',
          initiated_by: initiatedBy,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving signing session:', error);
      throw error;
    }
  }

  async updateSigningSessionStatus(envelopeId: string, status: string, completedDocumentUrl?: string) {
    try {
      const updateData: any = { status };
      if (completedDocumentUrl) {
        updateData.completed_document_url = completedDocumentUrl;
        updateData.completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('signing_sessions')
        .update(updateData)
        .eq('envelope_id', envelopeId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating signing session status:', error);
    }
  }

  async saveSigningInvitation(
    signingSessionId: string,
    recipientEmail: string,
    recipientName: string,
    recipientRole: string,
    customMessage?: string
  ) {
    try {
      const { data, error } = await supabase
        .from('signing_invitations')
        .insert({
          signing_session_id: signingSessionId,
          recipient_email: recipientEmail,
          recipient_name: recipientName,
          recipient_role: recipientRole,
          custom_message: customMessage,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving signing invitation:', error);
      throw error;
    }
  }

  async getDocumentWithSigningStatus(documentGenerationId: string) {
    try {
      const { data: docData, error: docError } = await supabase
        .from('document_generations')
        .select('*, signing_sessions(*)')
        .eq('id', documentGenerationId)
        .single();

      if (docError) throw docError;
      return docData;
    } catch (error) {
      console.error('Error fetching document with signing status:', error);
      return null;
    }
  }
}

export const documentSaveService = new DocumentSaveService();
